class ZinVoltProductHero {
  constructor(root) {
    this.root = root;
    this.dataset = root.dataset;
    this.labels = {
      add: this.dataset.labelAdd || 'In winkelwagen',
      soldOut: this.dataset.labelSoldOut || 'Uitverkocht',
      available: this.dataset.labelAvailable || 'Beschikbaar',
      unavailable: this.dataset.labelUnavailable || 'Tijdelijk niet beschikbaar',
      youSave: this.dataset.labelYouSave || 'Je bespaart [amount]',
      mediaPosition: this.dataset.labelMediaPosition || 'Media [index] van [total]',
      viewMedia: this.dataset.labelViewMedia || 'Bekijk media [index] van [total]',
      selectedFor: this.dataset.labelSelectedFor || '[product] geselecteerd voor [price].',
      selectedWithInstallation: this.dataset.labelSelectedWithInstallation || '[product] geselecteerd voor [price], inclusief installatie.',
      installationSelected: this.dataset.labelInstallationSelected || '[installation] geselecteerd. Totaalprijs [price].',
      installationRemoved: this.dataset.labelInstallationRemoved || '[installation] uitgevinkt. Productprijs [price].'
    };
    this.variants = [];
    this.currentVariant = null;
    this.currentIndex = Number(this.dataset.activeMediaIndex || 0);
    this.touchStartX = null;
    this.isAdding = false;
    this.variantGalleryTimer = null;
    this.variantGalleryCleanupTimer = null;
    this.init();
  }

  querySelector(selector) {
    return this.root.querySelector(selector);
  }

  querySelectorAll(selector) {
    return this.root.querySelectorAll(selector);
  }

  dispatchEvent(event) {
    return this.root.dispatchEvent(event);
  }

  formatLabel(template, replacements = {}) {
    return Object.entries(replacements).reduce(
      (label, [key, value]) => label.split(`[${key}]`).join(String(value)),
      template
    );
  }

  formatStickyProductTitle(variantTitle = '') {
    const title = String(variantTitle || '').trim();
    if (!title || title === 'Default Title') return this.productTitle;
    if (title.toLocaleLowerCase().includes(this.productTitle.toLocaleLowerCase())) return title;
    return `${this.productTitle} | ${title}`;
  }

  init() {
    this.track = this.querySelector('[data-gallery-track]');
    this.stage = this.querySelector('[data-gallery-stage]');
    this.allSlides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.allThumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.slides = [...this.allSlides];
    this.thumbs = [...this.allThumbs];
    this.galleryPrev = this.querySelector('[data-gallery-prev]');
    this.galleryNext = this.querySelector('[data-gallery-next]');
    this.galleryCounter = this.querySelector('.zv-ph__counter');
    this.galleryCurrent = this.querySelector('[data-gallery-current]');
    this.galleryTotal = this.querySelector('[data-gallery-total]');
    this.productForm = this.querySelector('product-form');
    this.form = this.querySelector('.zv-ph__form');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.variantRadios = Array.from(this.querySelectorAll('[data-variant-radio]'));
    this.price = this.querySelector('[data-product-price]');
    this.priceSaleRow = this.querySelector('[data-price-sale-row]');
    this.totalSaving = this.querySelector('[data-total-saving]');
    this.coreRegularPrice = this.querySelector('[data-core-regular-price]');
    this.coreRegularPriceProperty = this.querySelector('[data-core-regular-price-property]');
    this.coreTotalPriceProperty = this.querySelector('[data-core-total-price-property]');
    this.coreFinalTotal = this.querySelector('[data-core-final-total]');
    this.coreFinalProductPrice = this.querySelector('[data-core-final-product-price]');
    this.coreFinalProductLabel = this.querySelector('[data-core-final-product-label]');
    this.coreFinalInstallationRow = this.querySelector('[data-core-final-installation-row]');
    this.coreFinalInstallationPrice = this.querySelector('[data-core-final-installation-price]');
    this.selectedVariantText = this.querySelector('[data-selected-variant]');
    this.availability = this.querySelector('[data-availability]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.mobileBar = this.querySelector('[data-mobile-bar]');
    this.mobilePrice = this.querySelector('[data-mobile-price]');
    this.mobileVariant = this.querySelector('[data-mobile-variant]');
    this.stickyVariantSelect = this.querySelector('[data-sticky-variant-select]');
    this.mobileAdd = this.querySelector('[data-mobile-add]');
    this.mobileAddLabel = this.querySelector('[data-mobile-add-label]');
    this.status = this.querySelector('[data-variant-status]');
    this.installationOption = this.querySelector('[data-installation-option]');
    this.installationCheckbox = this.querySelector('[data-installation-checkbox]');
    this.installationVariantId = Number(this.dataset.installationVariantId || 0);
    this.installationPrice = Number(this.dataset.installationPrice || 0);
    this.installationComparePrice = Number(this.dataset.installationComparePrice || 0);
    this.installationLabel = this.dataset.installationLabel || 'Professionele installatie';
    this.isCorePreorder = this.dataset.isCorePreorder === 'true';
    this.dynamicVariantGallery = this.dataset.dynamicVariantGallery === 'true';
    this.installationOfferPrice = Number(this.dataset.installationOfferPrice || 0);
    this.installationRegularPrice = Number(this.dataset.installationRegularPrice || 0);
    this.productTitle = this.dataset.productTitle || this.querySelector('.zv-ph__title')?.textContent || 'Product';
    this.dynamicCheckout = this.querySelector('.zv-ph__dynamic-checkout');
    this.dynamicCheckoutInitiallyHidden = Boolean(this.dynamicCheckout?.hidden);
    this.spinner = this.querySelector('.loading-overlay__spinner');
    this.cart = document.querySelector('cart-drawer');

    const variantData = this.querySelector('[data-zv-variants]');
    if (variantData) {
      try {
        this.variants = JSON.parse(variantData.textContent);
      } catch (error) {
        console.error('ZinVolt-productvarianten konden niet worden geladen.', error);
      }
    }

    this.currentVariant = this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value)) || this.variants[0] || null;

    this.updateGalleryForVariant(this.currentVariant);
    this.bindGallery();
    this.bindVariants();
    this.bindQuantity();
    this.bindInstallation();
    this.bindMobileBar();
    this.goTo(this.currentIndex, false);
    this.updateDisplayedPrice();
  }

  updateGalleryForVariant(variant) {
    if (!this.dynamicVariantGallery) {
      this.slides = [...this.allSlides];
      this.thumbs = [...this.allThumbs];
    } else {
      const selectedMediaId = String(variant?.featured_media_id || '');
      const selectedSlides = [];
      const generalSlides = [];
      const selectedThumbs = [];
      const generalThumbs = [];

      this.allSlides.forEach((slide) => {
        const isVariantMedia = slide.dataset.variantMedia === 'true';
        const isSelectedVariantMedia = isVariantMedia && String(slide.dataset.mediaId) === selectedMediaId;
        const visible = !isVariantMedia || isSelectedVariantMedia;

        slide.hidden = !visible;
        slide.style.order = isSelectedVariantMedia ? '-1' : '';
        if (isSelectedVariantMedia) selectedSlides.push(slide);
        else if (!isVariantMedia) generalSlides.push(slide);
      });

      this.allThumbs.forEach((thumb) => {
        const isVariantMedia = thumb.dataset.variantMedia === 'true';
        const isSelectedVariantMedia = isVariantMedia && String(thumb.dataset.mediaId) === selectedMediaId;
        const visible = !isVariantMedia || isSelectedVariantMedia;

        thumb.hidden = !visible;
        thumb.style.order = isSelectedVariantMedia ? '-1' : '';
        if (isSelectedVariantMedia) selectedThumbs.push(thumb);
        else if (!isVariantMedia) generalThumbs.push(thumb);
      });

      this.slides = [...selectedSlides, ...generalSlides];
      this.thumbs = [...selectedThumbs, ...generalThumbs];
    }

    const total = this.slides.length;
    if (this.dynamicVariantGallery) {
      this.currentIndex = 0;
    } else if (total > 0) {
      this.currentIndex = Math.min(Math.max(this.currentIndex, 0), total - 1);
    }
    if (this.galleryTotal) this.galleryTotal.textContent = String(total);
    if (this.galleryCounter) this.galleryCounter.hidden = total === 0;
    if (this.galleryPrev) this.galleryPrev.hidden = total < 2;
    if (this.galleryNext) this.galleryNext.hidden = total < 2;

    this.thumbs.forEach((thumb, index) => {
      thumb.setAttribute('aria-label', this.formatLabel(this.labels.viewMedia, {
        index: index + 1,
        total
      }));
    });
  }

  switchVariantGallery(variant) {
    const applyVariantMedia = () => {
      this.updateGalleryForVariant(variant);
      this.goTo(0, false);
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.stage || prefersReducedMotion) {
      applyVariantMedia();
      return;
    }

    window.clearTimeout(this.variantGalleryTimer);
    window.clearTimeout(this.variantGalleryCleanupTimer);
    this.stage.classList.remove('is-variant-revealing');
    this.stage.classList.add('is-variant-fading');
    this.stage.setAttribute('aria-busy', 'true');

    this.variantGalleryTimer = window.setTimeout(() => {
      applyVariantMedia();
      this.stage.classList.remove('is-variant-fading');
      this.stage.classList.add('is-variant-revealing');
      this.stage.setAttribute('aria-busy', 'false');

      this.variantGalleryCleanupTimer = window.setTimeout(() => {
        this.stage?.classList.remove('is-variant-revealing');
      }, 320);
    }, 150);
  }

  bindGallery() {
    this.galleryPrev?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.galleryNext?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    this.allThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const thumbIndex = this.thumbs.indexOf(thumb);
        if (thumbIndex >= 0) this.goTo(thumbIndex);
      });
      thumb.addEventListener('keydown', (event) => {
        const thumbIndex = this.thumbs.indexOf(thumb);
        if (thumbIndex < 0) return;
        const keyTargets = {
          ArrowLeft: thumbIndex - 1,
          ArrowUp: thumbIndex - 1,
          ArrowRight: thumbIndex + 1,
          ArrowDown: thumbIndex + 1,
          Home: 0,
          End: this.thumbs.length - 1
        };
        if (!(event.key in keyTargets)) return;
        event.preventDefault();
        this.goTo(keyTargets[event.key]);
        this.thumbs[this.currentIndex]?.focus();
      });
    });

    this.stage?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      this.touchStartX = event.clientX;
    });

    this.stage?.addEventListener('pointerup', (event) => {
      if (this.touchStartX === null) return;
      const distance = event.clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(distance) < 45) return;
      this.goTo(distance > 0 ? this.currentIndex - 1 : this.currentIndex + 1);
    });

    this.stage?.addEventListener('pointercancel', () => {
      this.touchStartX = null;
    });
  }

  goTo(index, announce = true) {
    if (!this.slides.length || !this.track) return;
    const total = this.slides.length;
    this.currentIndex = (index + total) % total;
    this.track.style.setProperty('--zv-gallery-offset', `${this.currentIndex * -100}%`);

    const activeSlide = this.slides[this.currentIndex];
    const activeThumb = this.thumbs[this.currentIndex];

    this.allSlides.forEach((slide) => {
      const active = slide === activeSlide;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.inert = !active;
    });

    this.allThumbs.forEach((thumb) => {
      const active = thumb === activeThumb;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      thumb.tabIndex = active ? 0 : -1;
    });

    if (this.galleryCurrent) this.galleryCurrent.textContent = String(this.currentIndex + 1);
    if (this.galleryTotal) this.galleryTotal.textContent = String(total);
    /* Houd de actieve miniatuur zichtbaar zonder de volledige pagina te
       verplaatsen. scrollIntoView kan tijdens de eerste initialisatie op
       mobiel ook de verticale documentpositie aanpassen. */
    const thumbRail = activeThumb?.parentElement;
    if (activeThumb && thumbRail && typeof thumbRail.scrollTo === 'function') {
      const left = activeThumb.offsetLeft - ((thumbRail.clientWidth - activeThumb.clientWidth) / 2);
      const top = activeThumb.offsetTop - ((thumbRail.clientHeight - activeThumb.clientHeight) / 2);
      thumbRail.scrollTo({
        left: Math.max(0, left),
        top: Math.max(0, top),
        behavior: announce ? 'smooth' : 'auto'
      });
    }

    if (announce && this.status) {
      this.status.textContent = this.formatLabel(this.labels.mediaPosition, {
        index: this.currentIndex + 1,
        total
      });
    }
  }

  bindVariants() {
    this.variantRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const variant = this.variants.find((item) => String(item.id) === String(radio.value));
        if (variant) this.selectVariant(variant);
      });
    });

    this.stickyVariantSelect?.addEventListener('change', () => {
      const variant = this.variants.find((item) => String(item.id) === String(this.stickyVariantSelect.value));
      if (variant) this.selectVariant(variant);
    });
  }

  selectVariant(variant) {
    this.currentVariant = variant;
    if (this.variantInput) this.variantInput.value = variant.id;

    this.variantRadios.forEach((radio) => {
      const selected = String(radio.value) === String(variant.id);
      radio.checked = selected;
      radio.closest('.zv-ph__variant-card')?.classList.toggle('is-selected', selected);
    });

    const variantTitle = variant.title === 'Default Title' ? '' : variant.title;
    if (this.selectedVariantText) this.selectedVariantText.textContent = variantTitle;
    if (this.mobileVariant) this.mobileVariant.textContent = this.formatStickyProductTitle(variantTitle);
    if (this.stickyVariantSelect) this.stickyVariantSelect.value = String(variant.id);

    if (this.isCorePreorder && variant.regular_price) {
      const regularPrice = this.money(variant.regular_price);
      if (this.coreRegularPrice) this.coreRegularPrice.textContent = regularPrice;
      if (this.coreRegularPriceProperty) this.coreRegularPriceProperty.value = regularPrice;
    }

    this.updateDisplayedPrice();
    this.updateAvailability(Boolean(variant.available));
    this.updateQuantityRules(variant);

    if (this.dynamicVariantGallery) {
      this.switchVariantGallery(variant);
    } else if (variant.featured_media_id) {
      const mediaIndex = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(variant.featured_media_id));
      if (mediaIndex >= 0) this.goTo(mediaIndex, false);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    if (this.status) {
      const template = this.hasInstallationSelected() ? this.labels.selectedWithInstallation : this.labels.selectedFor;
      this.status.textContent = this.formatLabel(template, {
        product: variantTitle || this.productTitle,
        price: this.money(this.getCurrentTotalPrice())
      });
    }
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  bindInstallation() {
    if (!this.installationCheckbox || !this.installationVariantId || !this.form) return;

    this.installationCheckbox.addEventListener('change', () => this.updateInstallationState(true));
    this.installationCheckbox.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.installationCheckbox.click();
    });

    this.form.addEventListener('submit', (event) => this.handleInstallationSubmit(event), true);
    this.updateInstallationState(false);
  }

  updateInstallationState(announce = false) {
    const selected = this.hasInstallationSelected();
    this.installationOption?.classList.toggle('is-selected', selected);
    if (this.dynamicCheckout) {
      this.dynamicCheckout.hidden = selected ? true : this.dynamicCheckoutInitiallyHidden;
      this.dynamicCheckout.setAttribute('aria-hidden', selected ? 'true' : 'false');
    }

    this.updateDisplayedPrice();

    if (announce && this.status) {
      this.status.textContent = this.formatLabel(
        selected ? this.labels.installationSelected : this.labels.installationRemoved,
        {
          installation: this.installationLabel,
          price: this.money(this.getCurrentTotalPrice())
        }
      );
    }
  }

  hasInstallationSelected() {
    return Boolean(this.installationCheckbox?.checked && this.installationVariantId);
  }

  getCurrentTotalPrice() {
    const variantPrice = Number(this.currentVariant?.price || 0);
    return variantPrice + (this.hasInstallationSelected() ? this.installationPrice : 0);
  }

  getCoreFinalProductPrice() {
    return Number(this.currentVariant?.regular_price || this.currentVariant?.price || 0);
  }

  getCoreFinalTotalPrice() {
    return this.getCoreFinalProductPrice() + (this.hasInstallationSelected() ? this.installationOfferPrice : 0);
  }

  updateCoreFinalSummary() {
    if (!this.isCorePreorder || !this.currentVariant) return;

    const installationSelected = this.hasInstallationSelected();
    const productPrice = this.getCoreFinalProductPrice();
    const finalTotal = this.getCoreFinalTotalPrice();
    const variantTitle = this.currentVariant.title === 'Default Title' ? this.productTitle : this.currentVariant.title;

    if (this.coreRegularPrice) this.coreRegularPrice.textContent = this.money(productPrice);
    if (this.coreRegularPriceProperty) this.coreRegularPriceProperty.value = this.money(productPrice);
    if (this.coreTotalPriceProperty) this.coreTotalPriceProperty.value = this.money(finalTotal);
    if (this.coreFinalTotal) this.coreFinalTotal.textContent = this.money(finalTotal);
    if (this.coreFinalProductPrice) this.coreFinalProductPrice.textContent = this.money(productPrice);
    if (this.coreFinalProductLabel) this.coreFinalProductLabel.textContent = variantTitle;
    if (this.coreFinalInstallationRow) this.coreFinalInstallationRow.hidden = !installationSelected;
    if (this.coreFinalInstallationPrice) this.coreFinalInstallationPrice.textContent = this.money(this.installationOfferPrice);

  }

  getCurrentTotalComparePrice() {
    const variantPrice = Number(this.currentVariant?.price || 0);
    const variantComparePrice = Number(this.currentVariant?.compare_at_price || 0);
    const effectiveVariantComparePrice = Math.max(variantPrice, variantComparePrice);

    if (!this.hasInstallationSelected()) {
      return effectiveVariantComparePrice > variantPrice ? effectiveVariantComparePrice : 0;
    }

    const effectiveInstallationComparePrice = Math.max(this.installationPrice, this.installationComparePrice);
    const totalPrice = variantPrice + this.installationPrice;
    const totalComparePrice = effectiveVariantComparePrice + effectiveInstallationComparePrice;
    return totalComparePrice > totalPrice ? totalComparePrice : 0;
  }

  updateDisplayedPrice() {
    if (!this.currentVariant) return;

    const totalPrice = this.getCurrentTotalPrice();
    const totalComparePrice = this.isCorePreorder ? 0 : this.getCurrentTotalComparePrice();
    const totalSaving = Math.max(0, totalComparePrice - totalPrice);
    const hasCompare = totalComparePrice > totalPrice;

    if (this.price) this.price.textContent = this.money(totalPrice);
    if (this.mobilePrice) this.mobilePrice.textContent = this.money(totalPrice);

    if (this.priceSaleRow) this.priceSaleRow.classList.toggle('is-hidden', this.isCorePreorder || !hasCompare);
    if (this.totalSaving) {
      this.totalSaving.textContent = hasCompare
        ? this.formatLabel(this.labels.youSave, { amount: this.money(totalSaving) })
        : '';
    }

    this.updateCoreFinalSummary();
  }

  handleInstallationSubmit(event) {
    if (!this.hasInstallationSelected()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isAdding || !this.currentVariant?.available || this.addButton?.getAttribute('aria-disabled') === 'true') return;
    this.addProductWithInstallation();
  }

  async addProductWithInstallation() {
    const selectedProductVariantId = Number(this.variantInput?.value || this.currentVariant?.id || 0);
    const selectedQuantity = Math.max(1, Number(this.quantityInput?.value || 1));
    const selectedProductVariant = this.variants.find((variant) => Number(variant.id) === selectedProductVariantId) || this.currentVariant;

    if (!selectedProductVariantId || !selectedProductVariant || !this.installationVariantId) {
      this.showFormError('De gekozen uitvoering kon niet worden toegevoegd. Selecteer het product opnieuw.');
      return;
    }

    this.showFormError();
    this.setLoading(true);

    const payload = {
      items: [
        {
          id: selectedProductVariantId,
          quantity: selectedQuantity,
          ...(this.isCorePreorder ? {
            properties: {
              'Pre-order': 'Aanbetaling; wordt verrekend bij de eindbetaling',
              'Productprijs': this.money(selectedProductVariant.regular_price || 0),
              'Totale prijs gekozen configuratie': this.money((selectedProductVariant.regular_price || 0) + this.installationOfferPrice)
            }
          } : {})
        },
        {
          id: this.installationVariantId,
          quantity: 1,
          parent_id: selectedProductVariantId,
          properties: {
            'Installatie voor': this.productTitle,
            'Gekozen uitvoering': selectedProductVariant.title,
            ...(this.isCorePreorder ? {
              'Pre-order': 'Aanbetaling; wordt verrekend bij de eindbetaling',
              'Actieprijs installatie': this.money(this.installationOfferPrice),
              'Normale installatieprijs': this.money(this.installationRegularPrice)
            } : {})
          }
        }
      ]
    };

    if (this.cart) {
      payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }

    try {
      const shopifyRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${shopifyRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'De bestelling kon niet aan de winkelwagen worden toegevoegd.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'zv-product-hero',
          productVariantId: selectedProductVariantId
        });
      }

      if (this.cart) {
        this.cart.classList.remove('is-empty');
        this.cart.renderContents(result);
      } else {
        window.location = window.routes?.cart_url || `${shopifyRoot}cart`;
      }
    } catch (error) {
      console.error(`${this.productTitle} met ${this.installationLabel.toLowerCase()} kon niet aan de winkelwagen worden toegevoegd.`, error);
      this.showFormError(error.message || 'Er ging iets mis bij het toevoegen. Probeer het opnieuw.');
    } finally {
      this.setLoading(false);
    }
  }

  showFormError(message = false) {
    if (typeof this.productForm?.handleErrorMessage === 'function') {
      this.productForm.handleErrorMessage(message);
      return;
    }

    const wrapper = this.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !text) return;
    wrapper.toggleAttribute('hidden', !message);
    if (message) text.textContent = message;
  }

  setLoading(loading) {
    this.isAdding = loading;
    const available = Boolean(this.currentVariant?.available);

    if (this.addButton) {
      this.addButton.disabled = loading || !available;
      this.addButton.setAttribute('aria-disabled', loading || !available ? 'true' : 'false');
      this.addButton.toggleAttribute('aria-busy', loading);
      this.addButton.classList.toggle('loading', loading);
    }

    if (this.mobileAdd) {
      this.mobileAdd.disabled = loading || !available;
      this.mobileAdd.toggleAttribute('aria-busy', loading);
    }

    this.spinner?.classList.toggle('hidden', !loading);
  }

  updateAvailability(available) {
    if (this.availability) {
      this.availability.dataset.available = String(available);
      const strong = this.availability.querySelector('strong');
      if (strong) strong.textContent = available ? this.labels.available : this.labels.unavailable;
    }

    if (this.addButton) {
      this.addButton.disabled = !available;
      this.addButton.setAttribute('aria-disabled', available ? 'false' : 'true');
    }
    if (this.addLabel) this.addLabel.textContent = available ? this.labels.add : this.labels.soldOut;

    if (this.mobileAdd) this.mobileAdd.disabled = !available;
    if (this.mobileAddLabel) this.mobileAddLabel.textContent = available ? this.labels.add : this.labels.soldOut;
  }

  updateQuantityRules(variant) {
    if (!this.quantityInput) return;
    const min = Number(variant.quantity_min || 1);
    const max = Number(variant.quantity_max || 0);
    const step = Number(variant.quantity_step || 1);
    this.quantityInput.min = String(min);
    this.quantityInput.step = String(step);
    if (max > 0) this.quantityInput.max = String(max);
    else this.quantityInput.removeAttribute('max');
    if (Number(this.quantityInput.value) < min) this.quantityInput.value = String(min);
  }

  bindQuantity() {
    if (!this.quantityInput) return;
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
    this.quantityInput.addEventListener('change', () => this.clampQuantity());
  }

  changeQuantity(direction) {
    const step = Number(this.quantityInput.step || 1);
    this.quantityInput.value = String(Number(this.quantityInput.value || 1) + direction * step);
    this.clampQuantity();
  }

  clampQuantity() {
    const min = Number(this.quantityInput.min || 1);
    const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    const value = Math.max(min, Math.min(max, Number(this.quantityInput.value || min)));
    this.quantityInput.value = String(value);
  }

  bindMobileBar() {
    if (!this.mobileBar || !this.addButton) return;
    this.mobileAdd?.addEventListener('click', () => this.addButton.click());

    if (!('IntersectionObserver' in window)) return;
    this.primaryActionPassed = false;
    this.footerVisible = false;
    const updateVisibility = () => {
      const show = this.primaryActionPassed && !this.footerVisible;
      this.mobileBar.classList.toggle('is-visible', show);
      this.mobileBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    };

    this.mobileObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      this.primaryActionPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      updateVisibility();
    }, { threshold: 0.15 });
    this.mobileObserver.observe(this.addButton);

    const footer = document.querySelector('[data-zv-footer]');
    if (footer) {
      this.footerObserver = new IntersectionObserver((entries) => {
        this.footerVisible = entries.some((entry) => entry.isIntersecting);
        updateVisibility();
      }, { threshold: 0 });
      this.footerObserver.observe(footer);
    }
  }

  money(cents) {
    if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR'
      }).format(Number(cents) / 100);
    } catch (error) {
      return `€ ${(Number(cents) / 100).toFixed(2).replace('.', ',')}`;
    }
  }
}

document.querySelectorAll('[data-zv-product-hero]').forEach((element) => {
  if (!element.__zvProductHero) {
    element.__zvProductHero = new ZinVoltProductHero(element);
  }
});

document.addEventListener('shopify:section:load', (event) => {
  const element = event.target.querySelector('[data-zv-product-hero]') || (event.target.matches?.('[data-zv-product-hero]') ? event.target : null);
  if (element && !element.__zvProductHero) {
    element.__zvProductHero = new ZinVoltProductHero(element);
  }
});
